import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { login as loginSession } from "@/lib/auth";
import GMark from "@/components/GMark";

// AuthCallback handles the fragment Emergent's managed Google auth leaves at
// <origin>/dashboard#session_id=… after a successful Google consent. It:
//   1) reads session_id from the hash synchronously (once, guarded by useRef)
//   2) exchanges it for our JWT via POST /api/auth/google/session
//   3) either logs the seller in and lands on /dashboard, or forwards to the
//      signup wizard with google=1&email=…&name=… so brand-new users land in
//      onboarding with their Google email/name pre-filled.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH. The Google redirect must be built from
// `window.location.origin` inside `SellerAuth.js` — nothing to hardcode here.
export default function AuthCallback() {
  const nav = useNavigate();
  const location = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const params = new URLSearchParams((location.hash || "").replace(/^#/, ""));
    const sessionId = params.get("session_id");
    if (!sessionId) {
      nav("/seller/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        // Clear the hash so a page refresh doesn't retry the (single-use) session_id.
        window.history.replaceState(null, "", window.location.pathname);
        if (data.existing) {
          loginSession(data.token, data.seller);
          toast.success("Welcome back");
          nav("/dashboard", { replace: true });
        } else {
          const q = new URLSearchParams({
            google: "1",
            email: data.google_email || "",
            name: data.google_name || "",
          });
          nav(`/seller/signup?${q.toString()}`, { replace: true });
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Google sign-in failed. Please try again.");
        nav("/seller/login", { replace: true });
      }
    })();
  }, [location.hash, nav]);

  return (
    <div
      data-testid="auth-callback"
      className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#F5F0E5] text-[#20251F]"
    >
      <GMark size={40} className="rounded-lg" />
      <p className="font-mono-label text-xs uppercase tracking-widest text-[#3F674E]/80">
        Signing you in…
      </p>
    </div>
  );
}
